import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { css, cx } from 'emotion';
import mediumZoom from 'medium-zoom';

import { rte } from '../utils/styles';
import { breakpoints, baselineGrid } from '../utils/presets';
import { options, rhythm } from '../utils/typography';
import { toRem, toVW, mqObj as mq } from '../utils/functions';

import SEO from '../components/seo';
import InnerWrapper from '../components/inner-wrapper';
import ThemeHero from '../components/theme-hero';
import ThemeDetails from '../components/theme-details';
import ThemeFeatures from '../components/themes-features';

const bp = mq.lapLarge;

const theme = css``;

const themeBody = css`
  margin: ${toRem(baselineGrid * 8)} 0;

  ${mq.lapLarge} {
    margin: ${toRem(baselineGrid * 3)} 0;
  }

  ${mq.deskLarge} {
    margin: ${toRem(baselineGrid * 4)} 0;
  }
`;

const themeBodyWrapper = css`
  display: block;

  ${bp} {
    display: flex;
  }
`;

const column = css`
  margin: ${rhythm(options.blockMarginBottom)} 0;
`;

const themeMain = css`
  flex: 1 1 auto;

  ${bp} {
    padding-right: calc(${toRem(baselineGrid * 4)} + ${toVW(baselineGrid * 4)});
  }

  ${mq.deskLarge} {
    padding-right: calc(${toRem(baselineGrid * 4)} + ${toVW(baselineGrid * 6)});
  }
`;

const themeDescription = css``;

const themeAside = css`
  flex: 0 0 auto;

  @media (max-width: ${breakpoints.lapLarge - 1}px) {
    margin-top: ${toRem(baselineGrid * 12)};
  }

  ${bp} {
    width: calc(${toRem(baselineGrid * 30)} + 5vw);
    align-self: flex-start;
  }

  ${mq.desk} {
    width: calc(${toRem(baselineGrid * 30)} + 8vw);
  }

  @media (min-width: ${breakpoints.lapLarge}px) and (min-height: 769px) {
    position: sticky;
    top: calc(${toRem(baselineGrid * 10)} + ${rhythm(options.blockMarginBottom)});
  }
`;

class ThemePage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      imageZoomSelector:
        "[class$='themeDescription'] .gatsby-resp-image-image, [class$='themeDescription'] .remark-image-wrapper img",
    };

    this.setImageZoom = this.setImageZoom.bind(this);
  }

  setImageZoom() {
    this.zoom = mediumZoom(this.state.imageZoomSelector);
  }

  componentDidMount() {
    this.setImageZoom();
  }

  componentWillUnmount() {
    if (this.zoom) {
      this.zoom.detach();
      this.zoom = undefined;
    }
  }

  render() {
    const {
      data: {
        themeData,
        themeData: {
          internal: { contentDigest: themeID },
          html,
          frontmatter: {
            created,
            demos,
            details,
            excerpt,
            features,
            heroBackgroundColor,
            heroBackgroundImage,
            heroImages,
            name,
            platform,
            price,
            purchaseURL,
            tags,
            updated,
            version,
          },
        },
      },
      pathContext: { pathPrefix },
    } = this.props;

    const detailsProps = {
      created,
      demos,
      details,
      name,
      platform,
      price,
      purchaseURL,
      tags,
      updated,
      version,
    };

    const imageSharp =
      heroImages[0] && heroImages[0].desktop && heroImages[0].desktop.childImageSharp;

    return (
      <article className={theme}>
        <SEO
          key={`seo-${themeID}`}
          postData={themeData}
          postImage={imageSharp && imageSharp.sizes && imageSharp.sizes.src}
          pathPrefix={pathPrefix}
          isTheme={true}
        >
          <title>{name}</title>
        </SEO>

        <ThemeHero
          key={`theme-hero-${themeID}`}
          name={name}
          excerpt={excerpt}
          price={price}
          demos={demos}
          purchaseURL={purchaseURL}
          backgroundColor={heroBackgroundColor}
          backgroundImage={heroBackgroundImage}
          images={heroImages}
        />

        <div className={themeBody}>
          <InnerWrapper className={themeBodyWrapper} narrow>
            <section className={cx(column, themeMain, rte)}>
              <div className={themeDescription} dangerouslySetInnerHTML={{ __html: html }} />
            </section>
            <aside className={cx(column, themeAside)}>
              <ThemeDetails {...detailsProps} />
            </aside>
          </InnerWrapper>
          <ThemeFeatures name={name} features={features} />
        </div>
      </article>
    );
  }
}

ThemePage.propTypes = {
  data: PropTypes.object,
  pathContext: PropTypes.object,
};

export default ThemePage;

export const query = graphql`
  query ThemePageQuery($id: String!) {
    themeData: markdownRemark(id: { eq: $id }) {
      ...themeHero_details
      ...themeDetails_details
      ...themeFeatures_details
      internal {
        contentDigest
      }
      html
      fields {
        title
        slug
        datePublished: created
      }
      frontmatter {
        dateModified: updated
        seo {
          title
          description
        }
      }
    }
  }
`;



// WEBPACK FOOTER //
// ./src/templates/theme-page.js